import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Send, Bot, User, Loader2, FileText, FileSpreadsheet, 
  Presentation, Download, PawPrint, Stethoscope, Utensils,
  Baby, Calendar, AlertTriangle, BookOpen, TrendingUp, Settings
} from 'lucide-react';
import api from '@/lib/api';

// Suggestions de questions pour l'éleveur
const SUGGESTIONS_ELEVEUR = [
  "Quel est le calendrier vaccinal pour mes lapins ?",
  "Génère-moi un tableau Excel de suivi des gestations",
  "Crée un planning de vermifugation pour mes chevaux",
  "Quels sont les signes de la myxomatose ?",
  "Calcule la ration alimentaire pour 300 lapines reproductrices",
  "Génère un document PDF sur la réglementation cuniculture",
  "Quand dois-je sevrer mes lapereaux ?",
  "Fais-moi un PowerPoint sur les bonnes pratiques d'élevage",
  "Quel est le protocole de reproduction en bande ?",
  "Liste les vaccins obligatoires pour les chevaux de compétition"
];

export default function AgentEleveurPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Bonjour ! Je suis votre **Agent IA Éleveur** spécialisé dans la gestion des élevages de lapins et chevaux.

Je peux vous aider sur :
- 🐰 **Cuniculture** : reproduction, alimentation, santé, réglementation
- 🐴 **Équins** : soins, vaccination, vermifugation, reproduction
- 📊 **Documents** : Je peux générer des tableaux Excel, documents Word, PDF ou PowerPoint

Comment puis-je vous aider aujourd'hui ?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const scrollRef = useRef(null);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post('/ai/eleveur/chat', {
        message: userMessage,
        context: {
          type: 'elevage',
          specialites: ['lapin', 'cheval']
        }
      });

      const assistantMessage = response.data.response || response.data.message;
      setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);

      // Si un document a été généré
      if (response.data.document) {
        setGeneratedDocs(prev => [response.data.document, ...prev]);
        toast.success(`Document généré : ${response.data.document.nom}`);
      }
    } catch (error) {
      console.error('Erreur agent éleveur:', error);
      
      // Réponse locale si l'API n'est pas disponible
      const localResponse = generateLocalResponse(userMessage);
      setMessages(prev => [...prev, { role: 'assistant', content: localResponse }]);
    } finally {
      setLoading(false);
      setTimeout(() => {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Génération de réponse locale (fallback)
  const generateLocalResponse = (question) => {
    const q = question.toLowerCase();
    
    if (q.includes('vaccin') && q.includes('lapin')) {
      return `## Calendrier vaccinal lapins 🐰

**Vaccinations obligatoires :**

| Vaccin | Fréquence | Remarques |
|--------|-----------|-----------|
| **Myxomatose** | Annuelle | Primo-vaccination dès 5 semaines |
| **VHD (variant 1 et 2)** | Annuelle | Vaccin combiné Nobivac Myxo-RHD recommandé |
| **Pasteurellose** | Selon risque | En élevage intensif |

**Protocole recommandé :**
1. J0 : Primo-vaccination Myxo-RHD (5 semaines)
2. J30 : Rappel si nécessaire
3. Puis rappel annuel

*Voulez-vous que je génère un tableau Excel de suivi vaccinal ?*`;
    }
    
    if (q.includes('excel') || q.includes('tableau')) {
      return `Je peux générer plusieurs types de tableaux Excel pour vous :

📊 **Tableaux disponibles :**
- Suivi des gestations (dates saillie, mise-bas prévue, résultats)
- Registre vaccinal par animal
- Planning de reproduction en bande
- Suivi alimentaire et consommation
- Registre sanitaire obligatoire
- Inventaire du cheptel

Quel tableau souhaitez-vous que je génère ?`;
    }
    
    if (q.includes('gestation') || q.includes('reproduction')) {
      return `## Suivi de reproduction 🐰

**Lapins - Cycle de reproduction :**
- Durée gestation : **31 jours** (28-33)
- Sevrage : **35 jours**
- Remise à la reproduction : **11 jours post-partum** (système intensif) ou **35 jours** (système extensif)

**Planning type en bande (42 jours) :**
| Jour | Action |
|------|--------|
| J0 | Saillie / Insémination |
| J12-14 | Palpation (diagnostic gestation) |
| J25 | Installation boîte à nid |
| J31 | Mise-bas |
| J35 | Sevrage |
| J42 | Nouvelle saillie |

*Voulez-vous un document détaillé sur la conduite en bande ?*`;
    }
    
    if (q.includes('cheval') && q.includes('vaccin')) {
      return `## Calendrier vaccinal équins 🐴

**Vaccinations :**

| Vaccin | Fréquence | Obligatoire |
|--------|-----------|-------------|
| **Grippe équine** | 6 mois (compétition) / 12 mois (loisir) | ✅ Oui (rassemblements) |
| **Tétanos** | 2-3 ans | ⚠️ Très recommandé |
| **Rhinopneumonie** | 6 mois (juments gestantes) | Pour reproduction |
| **Rage** | Annuelle si zone à risque | Selon département |

**Vermifugation :**
- Minimum 4 fois/an
- Coproscopie recommandée avant traitement
- Alterner les molécules (ivermectine, moxidectine, fenbendazole)

*Besoin d'un planning personnalisé pour votre écurie ?*`;
    }
    
    if (q.includes('pdf') || q.includes('document') || q.includes('word')) {
      return `Je peux générer les documents suivants :

📄 **Documents PDF :**
- Fiche sanitaire d'élevage
- Protocole de biosécurité
- Guide alimentation par stade physiologique
- Réglementation cuniculture/équine

📝 **Documents Word :**
- Registre d'élevage vierge
- Procédures sanitaires
- Plan de prophylaxie

📊 **Tableaux Excel :**
- Suivi de reproduction
- Gestion des stocks aliments
- Comptabilité élevage

🎯 **Présentations PowerPoint :**
- Formation personnel
- Bonnes pratiques d'élevage
- Présentation sanitaire

Que souhaitez-vous générer ?`;
    }
    
    return `Je suis votre Agent IA Éleveur, spécialisé dans :

🐰 **Cuniculture :**
- Reproduction et génétique
- Alimentation et nutrition
- Santé et prophylaxie
- Réglementation et registres

🐴 **Équins :**
- Soins quotidiens
- Vaccination et vermifugation
- Reproduction
- Maréchalerie et dentisterie

📋 **Je peux générer :**
- Tableaux Excel de suivi
- Documents Word/PDF
- Présentations PowerPoint
- Planning et calendriers

Posez-moi votre question !`;
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="agent-eleveur-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
            <PawPrint className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Agent IA Éleveur</h1>
            <p className="text-sm text-muted-foreground">Spécialiste lapins & chevaux</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/agents/config', { state: { selectedAgent: 'technique' } })}
            data-testid="agent-settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" /> Paramètres
          </Button>
          <Badge variant="outline" className="gap-1">
            <Stethoscope className="w-3 h-3" /> Santé
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Utensils className="w-3 h-3" /> Alimentation
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Baby className="w-3 h-3" /> Reproduction
          </Badge>
        </div>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Zone de chat principale */}
        <Card className="flex-1 flex flex-col">
          <CardContent className="flex-1 flex flex-col p-4 min-h-0">
            {/* Messages */}
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`p-2 rounded-full ${msg.role === 'user' ? 'bg-primary' : 'bg-amber-500'}`}>
                      {msg.role === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <PawPrint className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div
                      className={`flex-1 p-4 rounded-xl ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground ml-12'
                          : 'bg-muted mr-12'
                      }`}
                    >
                      <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                        {msg.content}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3">
                    <div className="p-2 rounded-full bg-amber-500">
                      <PawPrint className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-muted p-4 rounded-xl">
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={scrollRef} />
              </div>
            </ScrollArea>

            {/* Suggestions */}
            <div className="flex flex-wrap gap-2 py-3 border-t mt-3">
              {SUGGESTIONS_ELEVEUR.slice(0, 4).map((suggestion, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion.length > 40 ? suggestion.slice(0, 40) + '...' : suggestion}
                </Button>
              ))}
            </div>

            {/* Input */}
            <div className="flex gap-2 pt-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Posez votre question sur l'élevage..."
                className="flex-1"
                disabled={loading}
                data-testid="eleveur-input"
              />
              <Button onClick={sendMessage} disabled={loading || !input.trim()}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Panneau latéral - Documents & Raccourcis */}
        <div className="w-80 flex flex-col gap-4">
          {/* Documents générés */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documents générés
              </CardTitle>
            </CardHeader>
            <CardContent>
              {generatedDocs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Demandez-moi de générer un document Excel, Word, PDF ou PowerPoint
                </p>
              ) : (
                <div className="space-y-2">
                  {generatedDocs.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        {doc.type === 'excel' && <FileSpreadsheet className="w-4 h-4 text-green-600" />}
                        {doc.type === 'word' && <FileText className="w-4 h-4 text-blue-600" />}
                        {doc.type === 'powerpoint' && <Presentation className="w-4 h-4 text-orange-600" />}
                        {doc.type === 'pdf' && <FileText className="w-4 h-4 text-red-600" />}
                        <span className="text-sm truncate">{doc.nom}</span>
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Raccourcis */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Raccourcis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start text-sm" onClick={() => handleSuggestionClick("Génère un tableau Excel de suivi des gestations")}>
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                Tableau gestations
              </Button>
              <Button variant="outline" className="w-full justify-start text-sm" onClick={() => handleSuggestionClick("Crée un planning de vaccination pour mes animaux")}>
                <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                Planning vaccins
              </Button>
              <Button variant="outline" className="w-full justify-start text-sm" onClick={() => handleSuggestionClick("Génère un PDF sur la réglementation élevage")}>
                <BookOpen className="w-4 h-4 mr-2 text-red-600" />
                Réglementation PDF
              </Button>
              <Button variant="outline" className="w-full justify-start text-sm" onClick={() => handleSuggestionClick("Fais-moi un PowerPoint de formation élevage")}>
                <Presentation className="w-4 h-4 mr-2 text-orange-600" />
                Formation PPT
              </Button>
            </CardContent>
          </Card>

          {/* Stats rapides */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Mon élevage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <p className="text-lg font-bold text-amber-600">300</p>
                  <p className="text-xs text-muted-foreground">Mères lapins</p>
                </div>
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-lg font-bold text-blue-600">5</p>
                  <p className="text-xs text-muted-foreground">Chevaux</p>
                </div>
                <div className="p-2 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                  <p className="text-lg font-bold text-pink-600">600</p>
                  <p className="text-xs text-muted-foreground">Engraissement</p>
                </div>
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-lg font-bold text-green-600">0</p>
                  <p className="text-xs text-muted-foreground">Alertes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
